import type { BotController } from "./bot";
import type { CompetitiveConfig } from "./config";
import { cloneSnake } from "./match-entities";
import { distance, normalizeAngle, vec, type Vec2 } from "./math";
import type { SeededRng } from "./rng";
import { findSafeSpawnPoint } from "./spawn";
import { createTrail } from "./trail";
import { snakeWidthForLength } from "./tuning";
import type { SnakeIntent, SnakeState } from "./types";

export const ENDLESS_RESPAWN_SECONDS = 5;
export const RESPAWN_PROTECTION_SECONDS = 5;

interface RespawnInput {
  readonly atSeconds: number;
  readonly respawnDueBySnakeId: Map<string, number>;
  readonly protectionUntilBySnakeId: Map<string, number>;
  readonly snakesById: Map<string, SnakeState>;
  readonly lastSpawnPointBySnakeId: Map<string, Vec2>;
  readonly intents: Map<string, SnakeIntent>;
  readonly botControllers: Map<string, BotController>;
  readonly config: CompetitiveConfig;
  readonly random: SeededRng;
}

export const processEndlessRespawns = (input: RespawnInput): void => {
  for (const [snakeId, dueAt] of [...input.respawnDueBySnakeId.entries()].sort(
    ([left], [right]) => left.localeCompare(right),
  )) {
    if (dueAt > input.atSeconds + 1e-9) continue;
    const snake = input.snakesById.get(snakeId);
    if (!snake || snake.alive) {
      input.respawnDueBySnakeId.delete(snakeId);
      continue;
    }
    const active = [...input.snakesById.values()]
      .sort((left, right) => left.id.localeCompare(right.id))
      .filter((candidate) => candidate.alive)
      .map((candidate) => {
        const cloned = cloneSnake(candidate);
        cloned.protectionRemainingSeconds = Math.max(
          0,
          (input.protectionUntilBySnakeId.get(candidate.id) ??
            input.atSeconds) - input.atSeconds,
        );
        return cloned;
      });
    const position = findSafeSpawnPoint(
      active,
      input.config.arenaRadius,
      input.random.between(-Math.PI, Math.PI),
    );
    if (!position) continue;

    snake.position = position;
    input.lastSpawnPointBySnakeId.set(snake.id, { ...position });
    snake.heading = normalizeAngle(
      distance(position, vec(0, 0)) < 1
        ? input.random.between(-Math.PI, Math.PI)
        : Math.atan2(-position.y, -position.x) +
            input.random.between(-0.35, 0.35),
    );
    snake.targetHeading = snake.heading;
    snake.length = 160;
    snake.width = snakeWidthForLength(160);
    snake.speed = input.config.baseSpeed;
    snake.alive = true;
    snake.boosting = false;
    snake.boostFragmentCarry = 0;
    snake.trail = createTrail(position);
    snake.spawnGeneration = (snake.spawnGeneration ?? 0) + 1;
    snake.respawnRemainingSeconds = 0;
    snake.protectionRemainingSeconds = RESPAWN_PROTECTION_SECONDS;
    input.intents.set(snake.id, {
      targetHeading: snake.heading,
      boosting: false,
    });
    input.botControllers.get(snake.id)?.reset();
    input.protectionUntilBySnakeId.set(
      snake.id,
      input.atSeconds + RESPAWN_PROTECTION_SECONDS,
    );
    input.respawnDueBySnakeId.delete(snake.id);
  }
};
